// Canonical contract. Kept byte-identical in Argus frontend and CDU Hub; tested on release.
export const QUESTION_TYPES = [
  "short",
  "paragraph",
  "single",
  "multiple",
  "dropdown",
  "rating",
  "scale",
  "number",
  "date",
  "time",
  "grid",
  "section",
] as const;
export type QuestionType = (typeof QUESTION_TYPES)[number];
export type Answer = string | number | string[] | Record<string, string>;
export type Answers = Record<string, Answer>;
export interface FeedbackQuestion {
  id: string;
  type: QuestionType;
  title: string;
  description: string;
  required: boolean;
  options: string[];
  rows: string[];
  min: number;
  max: number;
  condition: { questionId: string; equals: string } | null;
}
export interface FeedbackDesign {
  accent: string;
  banner: "landscape" | "festival" | "minimal";
  font: "sans" | "serif";
}
export interface FeedbackForm {
  id: string;
  event_id: string | null;
  event_title: string | null;
  event_status: string | null;
  after_event: boolean;
  award_points: boolean;
  reward_kind: "none" | "event_point" | "award" | "prefix";
  reward_id: number | null;
  reward_label: string;
  published_at: number | null;
  title: string;
  description: string;
  confirmation: string;
  questions: FeedbackQuestion[];
  design: FeedbackDesign;
  status: "draft" | "published" | "closed";
  closes_at: number | null;
  revision: number;
  response_count: number;
  created_at: number;
  updated_at: number;
}
export class FeedbackError extends Error {
  constructor(
    message: string,
    public questionId?: string,
  ) {
    super(message);
  }
}
const object = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === "object" && !Array.isArray(v);
function text(
  v: unknown,
  label: string,
  max: number,
  required = false,
): string {
  if (typeof v !== "string" || v.length > max || (required && !v.trim()))
    throw new FeedbackError(
      `${label} ${required ? "is required and " : ""}must be at most ${max} characters.`,
    );
  return v.trim();
}
function choices(v: unknown, label: string, max: number): string[] {
  if (!Array.isArray(v) || v.length > max)
    throw new FeedbackError(`${label} must contain at most ${max} entries.`);
  const values = v.map((x) => text(x, label, 200, true));
  if (new Set(values).size !== values.length)
    throw new FeedbackError(`${label} must be unique.`);
  return values;
}
export function validateDefinition(input: unknown) {
  if (!object(input)) throw new FeedbackError("Invalid form.");
  if (
    !Array.isArray(input.questions) ||
    input.questions.length < 1 ||
    input.questions.length > 50
  )
    throw new FeedbackError("Add between 1 and 50 questions.");
  const seen = new Map<string, FeedbackQuestion>();
  const questions: FeedbackQuestion[] = input.questions.map((q: unknown) => {
    if (
      !object(q) ||
      typeof q.id !== "string" ||
      !/^[a-zA-Z0-9_-]{1,64}$/.test(q.id) ||
      seen.has(q.id) ||
      ["__proto__", "constructor", "prototype"].includes(q.id)
    )
      throw new FeedbackError("Question IDs must be unique.");
    if (!QUESTION_TYPES.includes(q.type as QuestionType))
      throw new FeedbackError("Unknown question type.");
    const type = q.type as QuestionType;
    const options = choices(q.options ?? [], "Options", 30);
    const rows = choices(q.rows ?? [], "Grid rows", 20);
    if (
      ["single", "multiple", "dropdown", "grid"].includes(type) &&
      options.length < 2
    )
      throw new FeedbackError("Choice questions need at least two options.");
    if (type === "grid" && !rows.length)
      throw new FeedbackError("A grid needs at least one row.");
    const min = typeof q.min === "number" ? q.min : 1,
      max = typeof q.max === "number" ? q.max : 5;
    if (
      ![min, max].every(Number.isFinite) ||
      min > max ||
      Math.abs(min) > 1e9 ||
      Math.abs(max) > 1e9
    )
      throw new FeedbackError("Enter a valid minimum and maximum.");
    if (
      ["rating", "scale"].includes(type) &&
      (!Number.isInteger(min) ||
        !Number.isInteger(max) ||
        min < 0 ||
        max > 10 ||
        min === max)
    )
      throw new FeedbackError(
        "Scales must use whole numbers between 0 and 10.",
      );
    let condition: FeedbackQuestion["condition"] = null;
    if (q.condition != null) {
      if (
        !object(q.condition) ||
        typeof q.condition.questionId !== "string" ||
        typeof q.condition.equals !== "string"
      )
        throw new FeedbackError("Invalid question condition.");
      const parent = seen.get(q.condition.questionId);
      if (
        !parent ||
        !["single", "dropdown", "multiple"].includes(parent.type) ||
        !parent.options.includes(q.condition.equals)
      )
        throw new FeedbackError(
          "Conditions must refer to an earlier choice question and an existing option.",
        );
      condition = { questionId: parent.id, equals: q.condition.equals };
    }
    const result: FeedbackQuestion = {
      id: q.id,
      type,
      title: text(q.title, "Question title", 300, true),
      description: text(q.description ?? "", "Question description", 2000),
      required: q.required === true && type !== "section",
      options,
      rows,
      min,
      max,
      condition,
    };
    seen.set(result.id, result);
    return result;
  });
  if (!questions.some((q) => q.type !== "section"))
    throw new FeedbackError("Add at least one answerable question.");
  if (
    !object(input.design) ||
    typeof input.design.accent !== "string" ||
    !/^#[0-9a-f]{6}$/i.test(input.design.accent) ||
    !["landscape", "festival", "minimal"].includes(
      String(input.design.banner),
    ) ||
    !["sans", "serif"].includes(String(input.design.font))
  )
    throw new FeedbackError("Invalid form design.");
  if (!["draft", "published", "closed"].includes(String(input.status)))
    throw new FeedbackError("Invalid form status.");
  if (
    input.closes_at != null &&
    (typeof input.closes_at !== "number" ||
      !Number.isSafeInteger(input.closes_at) ||
      input.closes_at < 0)
  )
    throw new FeedbackError("Invalid closing date.");
  const event_id =
    input.event_id == null || input.event_id === ""
      ? null
      : text(input.event_id, "Event", 64, true);
  const reward_kind =
    input.reward_kind ?? (input.award_points === true ? "event_point" : "none");
  if (
    typeof reward_kind !== "string" ||
    !["none", "event_point", "award", "prefix"].includes(reward_kind)
  )
    throw new FeedbackError("Choose a valid reward type.");
  const reward_id =
    reward_kind === "award" || reward_kind === "prefix"
      ? input.reward_id
      : null;
  if (
    (reward_kind === "award" || reward_kind === "prefix") &&
    (typeof reward_id !== "number" ||
      !Number.isSafeInteger(reward_id) ||
      reward_id <= 0)
  )
    throw new FeedbackError("Select an award or prefix for this reward.");
  if (!event_id && (input.after_event || reward_kind === "event_point"))
    throw new FeedbackError("Link an event before enabling event settings.");
  if (reward_kind === "event_point" && !input.after_event)
    throw new FeedbackError("Point rewards require the event to be completed.");
  return {
    title: text(input.title, "Form title", 200, true),
    description: text(input.description ?? "", "Description", 5000),
    confirmation: text(
      input.confirmation ?? "Thanks for sharing your thoughts!",
      "Confirmation",
      1000,
      true,
    ),
    questions,
    design: {
      accent: input.design.accent,
      banner: input.design.banner as FeedbackDesign["banner"],
      font: input.design.font as FeedbackDesign["font"],
    },
    status: input.status as FeedbackForm["status"],
    closes_at: (input.closes_at as number | null) ?? null,
    event_id,
    after_event: input.after_event === true,
    award_points: reward_kind === "event_point",
    reward_kind: reward_kind as FeedbackForm["reward_kind"],
    reward_id: reward_id as number | null,
  };
}
export function visibleQuestions(
  questions: FeedbackQuestion[],
  answers: Answers,
): FeedbackQuestion[] {
  const visible = new Set<string>();
  return questions.filter((q) => {
    const c = q.condition,
      value = c ? answers[c.questionId] : undefined;
    const show =
      !c ||
      (visible.has(c.questionId) &&
        (Array.isArray(value) ? value.includes(c.equals) : value === c.equals));
    if (show) visible.add(q.id);
    return show;
  });
}
export function validateAnswers(
  questions: FeedbackQuestion[],
  input: unknown,
): Answers {
  if (!object(input)) throw new FeedbackError("Invalid answers.");
  if (
    Object.keys(input).some(
      (id) => !questions.some((q) => q.id === id && q.type !== "section"),
    )
  )
    throw new FeedbackError("Unknown question in response.");
  const answers: Answers = {};
  for (const q of questions) {
    if (q.condition) {
      const parent = answers[q.condition.questionId];
      if (
        !(Array.isArray(parent)
          ? parent.includes(q.condition.equals)
          : parent === q.condition.equals)
      )
        continue;
    }
    if (q.type === "section") continue;
    const value = input[q.id];
    const missing =
      value == null ||
      value === "" ||
      (typeof value === "string" && !value.trim()) ||
      (Array.isArray(value) && !value.length) ||
      (object(value) && !Object.keys(value).length);
    if (missing) {
      if (q.required)
        throw new FeedbackError(`Please answer “${q.title}”.`, q.id);
      continue;
    }
    const invalid = () => {
      throw new FeedbackError(`Check your answer to “${q.title}”.`, q.id);
    };
    if (["rating", "scale", "number"].includes(q.type)) {
      if (
        typeof value !== "number" ||
        !Number.isFinite(value) ||
        value < q.min ||
        value > q.max ||
        (q.type !== "number" && !Number.isInteger(value))
      )
        invalid();
    } else if (q.type === "multiple") {
      if (
        !Array.isArray(value) ||
        value.length > q.options.length ||
        new Set(value).size !== value.length ||
        value.some((v) => typeof v !== "string" || !q.options.includes(v))
      )
        invalid();
    } else if (q.type === "grid") {
      if (
        !object(value) ||
        Object.keys(value).some((r) => !q.rows.includes(r)) ||
        Object.values(value).some(
          (v) => typeof v !== "string" || !q.options.includes(v),
        ) ||
        (q.required && q.rows.some((r) => !value[r]))
      )
        invalid();
    } else {
      if (
        typeof value !== "string" ||
        value.length > (q.type === "paragraph" ? 10000 : 1000)
      )
        invalid();
      if (
        ["single", "dropdown"].includes(q.type) &&
        !q.options.includes(value as string)
      )
        invalid();
      if (
        q.type === "date" &&
        (!/^\d{4}-\d{2}-\d{2}$/.test(value as string) ||
          !Number.isFinite(Date.parse(value as string)) ||
          new Date(value as string).toISOString().slice(0, 10) !== value)
      )
        invalid();
      if (
        q.type === "time" &&
        !/^([01]\d|2[0-3]):[0-5]\d$/.test(value as string)
      )
        invalid();
    }
    answers[q.id] =
      typeof value === "string" ? value.trim() : (value as Answer);
  }
  return answers;
}
export function csvCell(value: unknown): string {
  let s =
    typeof value === "object" && value !== null
      ? JSON.stringify(value)
      : String(value ?? "");
  if (/^[\s]*[=+@-]/.test(s) || /^[\t\r\n]/.test(s)) s = "'" + s;
  return '"' + s.replace(/"/g, '""') + '"';
}
